/**
 * High-quality client-side image resizing using Canvas API.
 * Uses multi-step downsampling for small targets to avoid blurriness.
 */

/**
 * Resize an ImageBitmap to a square canvas at targetSize × targetSize.
 * Returns a Canvas element (not yet a blob).
 */
function drawToCanvas(bitmap, targetSize, options = {}) {
  const {
    exportMode = 'crop', // 'crop' | 'contain' | 'stretch'
    bgColor = '#ffffff',
    padding = 0,          // percentage 0–20
    preserveTransparency = true,
  } = options;

  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext('2d');

  // Background fill
  if (!preserveTransparency || exportMode === 'contain') {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, targetSize, targetSize);
  }

  const padPx = Math.round(targetSize * (padding / 100));
  const drawSize = targetSize - padPx * 2;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (exportMode === 'crop') {
    // Center-crop: take the largest square from the center of the source
    const srcSide = Math.min(bitmap.width, bitmap.height);
    const srcX = (bitmap.width - srcSide) / 2;
    const srcY = (bitmap.height - srcSide) / 2;
    ctx.drawImage(bitmap, srcX, srcY, srcSide, srcSide, padPx, padPx, drawSize, drawSize);
  } else if (exportMode === 'contain') {
    // Contain: scale down proportionally, center in canvas
    const scale = Math.min(drawSize / bitmap.width, drawSize / bitmap.height);
    const scaledW = bitmap.width * scale;
    const scaledH = bitmap.height * scale;
    const x = padPx + (drawSize - scaledW) / 2;
    const y = padPx + (drawSize - scaledH) / 2;
    ctx.drawImage(bitmap, x, y, scaledW, scaledH);
  } else {
    // Stretch: fill entire draw area
    ctx.drawImage(bitmap, padPx, padPx, drawSize, drawSize);
  }

  return canvas;
}

/**
 * Multi-step downsample: iteratively halve the image to keep sharpness.
 * Only applied when source is more than 2× larger than the target.
 */
async function highQualityResize(bitmap, targetSize) {
  const srcSize = Math.min(bitmap.width, bitmap.height);
  if (targetSize >= srcSize / 2) return bitmap; // Direct resize is fine

  let current = bitmap;
  let currentSize = srcSize;

  while (currentSize > targetSize * 2) {
    const nextSize = Math.max(Math.round(currentSize / 2), targetSize);
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = nextSize;
    tmpCanvas.height = nextSize;
    const tmpCtx = tmpCanvas.getContext('2d');
    tmpCtx.imageSmoothingEnabled = true;
    tmpCtx.imageSmoothingQuality = 'high';
    // Center-crop at each step to keep square aspect for the intermediate pass
    const srcSide = Math.min(current.width || current.videoWidth || nextSize * 2,
                             current.height || current.videoHeight || nextSize * 2);
    const srcX = ((current.width || srcSide) - srcSide) / 2;
    const srcY = ((current.height || srcSide) - srcSide) / 2;
    tmpCtx.drawImage(current, srcX, srcY, srcSide, srcSide, 0, 0, nextSize, nextSize);

    if (current !== bitmap) current.close?.();
    current = await createImageBitmap(tmpCanvas);
    currentSize = nextSize;
  }

  return current;
}

/**
 * Resize a source File/Blob to a square PNG blob at targetSize × targetSize.
 */
export async function resizeImageToBlob(sourceBitmap, targetSize, options = {}) {
  const intermediate = await highQualityResize(sourceBitmap, targetSize);
  const canvas = drawToCanvas(intermediate, targetSize, options);
  if (intermediate !== sourceBitmap) intermediate.close?.();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error(`Failed to create blob at ${targetSize}px`));
      },
      'image/png'
    );
  });
}

/**
 * Load a File into an ImageBitmap.
 */
export async function fileToImageBitmap(file) {
  return createImageBitmap(file);
}

/**
 * Convert a Blob to a data URL (for preview).
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

/**
 * Get image dimensions from a File without fully decoding it.
 */
export async function getImageDimensions(file) {
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  bitmap.close();
  return { width, height };
}
